import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Mic, Video, Trash2, Calendar, Play } from "lucide-react";
import { Button } from "../ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { RecordingData } from "../../hooks/useMediaRecorder";
import { RecordingDialog } from "./RecordingDialog";
import { AutoPlaySettings, AutoPlayConfig } from "./AutoPlaySettings";

interface RecordingPlayerProps {
  recording: RecordingData;
  type: "audio" | "video";
  onDelete?: () => void;
  compact?: boolean;
  readOnly?: boolean;
  /** Auto-play configuration (only shown when not readOnly and in additionalInfo context) */
  autoPlayConfig?: AutoPlayConfig;
  /** Callback when auto-play settings change */
  onAutoPlayChange?: (config: AutoPlayConfig) => void;
  /** Whether to show auto-play settings */
  showAutoPlaySettings?: boolean;
  /** Hide file size display (for cleaner engagement view) */
  hideFileSize?: boolean;
  /** Show video thumbnail instead of just icon */
  showThumbnail?: boolean;
}

export const RecordingPlayer = ({
  recording,
  type,
  onDelete,
  compact = false,
  readOnly = false,
  autoPlayConfig,
  onAutoPlayChange,
  showAutoPlaySettings = false,
  hideFileSize = false,
  showThumbnail = false,
}: RecordingPlayerProps) => {
  const { t } = useTranslation();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showPlaybackDialog, setShowPlaybackDialog] = useState(false);

  const handleDelete = () => {
    // Clean up blob URL
    if (recording.blobUrl) {
      URL.revokeObjectURL(recording.blobUrl);
    }
    onDelete?.();
    setShowDeleteDialog(false);
  };

  // Determine if delete button should be shown
  const showDeleteButton = !readOnly && onDelete !== undefined;

  // Format time as MM:SS
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Format date
  const formatDate = (isoString: string): string => {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return t("just_now", "Just now");
    if (diffMins < 60)
      return t("min_ago", "{{count}} min ago", { count: diffMins });

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) {
      return diffHours === 1
        ? t("hour_ago", "1 hour ago")
        : t("hours_ago", "{{count}} hours ago", { count: diffHours });
    }

    return date.toLocaleDateString();
  };

  return (
    <>
      <div
        className={
          compact
            ? "border rounded-md p-2 bg-muted/20 cursor-pointer hover:bg-muted/30 transition-colors"
            : "border rounded-lg p-3 bg-muted/20 space-y-2 cursor-pointer hover:bg-muted/30 transition-colors"
        }
        onClick={() => setShowPlaybackDialog(true)}
      >
        {compact ? (
          // Compact view - with optional thumbnail for video
          <div className="flex items-center gap-2">
            {/* Video thumbnail or icon */}
            {showThumbnail && type === "video" && recording.url ? (
              <div className="relative flex-shrink-0 w-16 h-10 overflow-hidden bg-black rounded">
                <video
                  src={recording.url}
                  className="object-cover w-full h-full"
                  preload="metadata"
                  muted
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                  <Play className="w-4 h-4 text-white" />
                </div>
              </div>
            ) : type === "audio" ? (
              <Mic className="flex-shrink-0 w-4 h-4 text-primary" />
            ) : (
              <Video className="flex-shrink-0 w-4 h-4 text-purple-600" />
            )}
            <div className="flex items-center flex-1 min-w-0 gap-2 text-xs text-muted-foreground">
              <span className="whitespace-nowrap">
                {formatTime(recording.duration)}
              </span>
              {!hideFileSize && (
                <>
                  <span>•</span>
                  <span className="whitespace-nowrap">
                    {(recording.size / 1024).toFixed(1)} KB
                  </span>
                </>
              )}
            </div>
            <div className="flex items-center flex-shrink-0 gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowPlaybackDialog(true);
                }}
                className="w-6 h-6 p-0 text-primary hover:text-primary hover:bg-primary/10"
                title={t("play_recording", "Play recording")}
              >
                <Play className="w-3 h-3" />
              </Button>
              {showDeleteButton && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDeleteDialog(true);
                  }}
                  className="w-6 h-6 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                  title={t("delete_recording", "Delete recording")}
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              )}
            </div>
          </div>
        ) : (
          // Full view with thumbnail/preview
          <>
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {type === "audio" ? (
                  <Mic className="w-4 h-4 text-primary" />
                ) : (
                  <Video className="w-4 h-4 text-purple-600" />
                )}
                <span className="text-sm font-medium capitalize">
                  {type === "audio"
                    ? t("audio_recording", "Audio Recording")
                    : t("video_recording", "Video Recording")}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowPlaybackDialog(true);
                  }}
                  className="w-8 h-8 p-0 text-primary hover:text-primary hover:bg-primary/10"
                  title={t("play_recording", "Play recording")}
                >
                  <Play className="w-4 h-4" />
                </Button>
                {showDeleteButton && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowDeleteDialog(true);
                    }}
                    className="w-8 h-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                    title={t("delete_recording", "Delete recording")}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>

            {/* Thumbnail/Preview */}
            <div className="flex items-center justify-center p-4 rounded-md bg-background">
              {type === "audio" ? (
                <div className="flex items-center gap-3 text-muted-foreground">
                  <Mic className="w-8 h-8" />
                  <div className="flex-1">
                    <div className="w-full h-2 rounded-full bg-muted" />
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center text-muted-foreground">
                  <Video className="w-12 h-12" />
                </div>
              )}
            </div>

            {/* Metadata */}
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <div className="flex items-center gap-3">
                <span>{formatTime(recording.duration)}</span>
                <span>•</span>
                <span>{(recording.size / 1024).toFixed(1)} KB</span>
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                <span>{formatDate(recording.recordedAt)}</span>
              </div>
            </div>

            {/* Auto-play settings (only in edit mode) */}
            {showAutoPlaySettings && !readOnly && autoPlayConfig && onAutoPlayChange && (
              <div
                className="pt-3 mt-3 border-t"
                onClick={(e) => e.stopPropagation()}
              >
                <AutoPlaySettings
                  config={autoPlayConfig}
                  onChange={onAutoPlayChange}
                  compact
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Playback Dialog */}
      <RecordingDialog
        open={showPlaybackDialog}
        onOpenChange={setShowPlaybackDialog}
        type={type}
        onComplete={() => {}}
        existingRecording={recording}
        mode="playback"
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("delete_recording_title", "Delete Recording?")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                "delete_recording_description",
                "Are you sure you want to delete this {{type}}? This action cannot be undone.",
                {
                  type:
                    type === "audio"
                      ? t("audio_recording", "Audio Recording")
                      : t("video_recording", "Video Recording"),
                },
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel", "Cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive hover:bg-destructive/90"
            >
              {t("delete", "Delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
